import { jsxs, jsx } from "react/jsx-runtime";
const WIDGET_KEYFRAMES = `
@keyframes voice-widget-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;
const FG = "var(--voice-widget-fg, #000000)";
const MUTED = "rgba(0, 0, 0, 0.5)";
const BORDER = "#e8e8ed";
const SOFT_BG = "#f6f8fa";
const CARD_BG = "#ffffff";
const STAR_GOLD = "#ffbd32";
const STAR_EMPTY = "#e8e8ed";
const THEME_PALETTE = {
  positive: { bg: "#f8fcf9", fg: "#71c184" },
  caution: { bg: "rgba(255, 189, 50, 0.05)", fg: "#ffbd32" },
  negative: { bg: "#fcebeb", fg: "#791f1f" }
};
const STAR_PATH = "M13.2986 5.03851C13.2113 4.75677 12.9718 4.55668 12.6885 4.53004L8.84014 4.16541L7.3184 0.448707C7.2062 0.17632 6.95065 0 6.66673 0C6.38281 0 6.12726 0.17632 6.01506 0.449347L4.49332 4.16541L0.644343 4.53004C0.361543 4.55732 0.12269 4.75679 0.0348901 5.03851C-0.0528966 5.32024 0.0281701 5.62924 0.242103 5.82403L3.15101 8.48611L2.29324 12.4289C2.23048 12.7188 2.3383 13.0185 2.56881 13.1924C2.69272 13.2858 2.83768 13.3333 2.98386 13.3333C3.1099 13.3333 3.23493 13.2979 3.34713 13.2278L6.6667 11.1575L9.98506 13.2278C10.2279 13.3803 10.534 13.3664 10.764 13.1924C10.9946 13.018 11.1023 12.7182 11.0396 12.4289L10.1818 8.48612L13.0907 5.82457C13.3047 5.62925 13.3863 5.32077 13.2985 5.03852L13.2986 5.03851Z";
function StarGlyph({ size, color }) {
  return /* @__PURE__ */ jsx("svg", { width: size, height: size, viewBox: "0 0 13.3333 13.3333", fill: "none", "aria-hidden": "true", style: { display: "block" }, children: /* @__PURE__ */ jsx("path", { d: STAR_PATH, fill: color, fillRule: "evenodd", clipRule: "evenodd" }) });
}
function Star({ size, kind }) {
  if (kind !== "half") {
    return /* @__PURE__ */ jsx(StarGlyph, { size, color: kind === "full" ? STAR_GOLD : STAR_EMPTY });
  }
  return /* @__PURE__ */ jsxs("span", { style: { position: "relative", display: "inline-block", width: size, height: size }, children: [
    /* @__PURE__ */ jsx(StarGlyph, { size, color: STAR_EMPTY }),
    /* @__PURE__ */ jsx(
      "span",
      {
        style: {
          position: "absolute",
          inset: 0,
          width: "50%",
          overflow: "hidden"
        },
        children: /* @__PURE__ */ jsx(StarGlyph, { size, color: STAR_GOLD })
      }
    )
  ] });
}
function StarRow({ stars, size = 16 }) {
  const full = Math.max(0, Math.min(5, Math.floor(stars)));
  const hasHalf = stars - full >= 0.5 && full < 5;
  return /* @__PURE__ */ jsx(
    "span",
    {
      style: { display: "inline-flex", alignItems: "center", gap: 2 },
      role: "img",
      "aria-label": `${stars} out of 5 stars`,
      children: Array.from({ length: 5 }, (_, i) => {
        const kind = i < full ? "full" : i === full && hasHalf ? "half" : "empty";
        return /* @__PURE__ */ jsx(Star, { size, kind }, i);
      })
    }
  );
}
function CheckIcon({ size = 20, color = FG }) {
  return /* @__PURE__ */ jsx("svg", { width: size, height: size, viewBox: "0 0 20 20", fill: "none", "aria-hidden": "true", style: { display: "block" }, children: /* @__PURE__ */ jsx(
    "path",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M7.59511 16.4286C7.39411 16.4286 7.19308 16.35 7.03996 16.1936L2.23034 11.2814C1.92322 10.9678 1.92322 10.4609 2.23034 10.1474C2.53733 9.83385 3.03351 9.83385 3.34066 10.1474L7.59511 14.4925L16.6595 5.23515C16.9665 4.92162 17.4626 4.92162 17.7698 5.23515C18.0767 5.54881 18.0767 6.05559 17.7698 6.36926L8.15039 16.1936C7.99727 16.35 7.79613 16.4286 7.59511 16.4286Z",
      fill: color
    }
  ) });
}
function DotIcon({ size = 20, color = FG }) {
  return /* @__PURE__ */ jsx("svg", { width: size, height: size, viewBox: "0 0 20 20", fill: "none", "aria-hidden": "true", style: { display: "block" }, children: /* @__PURE__ */ jsx("circle", { cx: "10", cy: "10", r: "3", fill: color }) });
}
function LensIcon({ size = 32, color = FG }) {
  return /* @__PURE__ */ jsxs("svg", { width: size, height: size, viewBox: "0 0 32 32", fill: "none", "aria-hidden": "true", style: { display: "block" }, children: [
    /* @__PURE__ */ jsx("rect", { width: "32", height: "32", rx: "5", fill: SOFT_BG }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M12.6679 6C8.99117 6 6 8.99144 6 12.6679C6 16.3445 8.99144 19.336 12.6679 19.336C14.3234 19.336 15.8365 18.7277 17.0039 17.7251L18.1419 18.8632L17.4366 19.5684C17.3393 19.6648 17.2844 19.796 17.2844 19.9331C17.2844 20.07 17.3393 20.2014 17.4366 20.2976L22.9907 25.8517C23.087 25.9491 23.2183 26.0039 23.3554 26.0039C23.4923 26.0039 23.6237 25.9491 23.72 25.8517L25.8518 23.7118C25.9467 23.6158 26 23.4862 26 23.3512C26 23.2162 25.9467 23.0865 25.8518 22.9905L20.2977 17.4287C20.1859 17.318 20.0293 17.2647 19.873 17.2844C19.7582 17.2968 19.6508 17.3477 19.5685 17.4287L18.8633 18.1339L17.7331 17.0039C18.7328 15.8375 19.3361 14.3209 19.3361 12.6679C19.3361 8.99117 16.3446 6 12.6679 6H12.6679ZM12.6679 7.0258C15.79 7.0258 18.3101 9.54586 18.3101 12.6681C18.3101 15.7903 15.7901 18.3104 12.6679 18.3104C9.54562 18.3104 7.02556 15.7903 7.02556 12.6681C7.02556 9.54586 9.54562 7.0258 12.6679 7.0258ZM19.9371 18.5187L20.8346 19.4163L19.4242 20.8269L18.5265 19.9292L19.9371 18.5187ZM21.564 20.1457L24.7698 23.3515L23.3592 24.762L20.1534 21.5562L21.564 20.1457Z",
        fill: color
      }
    )
  ] });
}
function BulletIcon({ tone, size = 20 }) {
  return tone === "positive" ? /* @__PURE__ */ jsx(CheckIcon, { size }) : /* @__PURE__ */ jsx(DotIcon, { size });
}
const SECTION_LABEL = {
  fontSize: 16,
  fontWeight: 600,
  color: FG,
  letterSpacing: "0.08px",
  margin: 0
};
function HighlightCard({ excerpt }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        background: SOFT_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10
      },
      children: [
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }, children: [
          excerpt.title && /* @__PURE__ */ jsx("span", { style: { fontSize: 14, fontWeight: 600, color: FG, letterSpacing: "0.08px", lineHeight: "24px" }, children: excerpt.title }),
          /* @__PURE__ */ jsx("span", { style: { flexShrink: 0 }, children: /* @__PURE__ */ jsx(StarRow, { stars: excerpt.stars, size: 16 }) })
        ] }),
        /* @__PURE__ */ jsx("p", { style: { margin: 0, fontSize: 14, fontWeight: 500, color: FG, letterSpacing: "0.08px", lineHeight: "24px" }, children: excerpt.snippet })
      ]
    }
  );
}
function InsightCard({ summary }) {
  const { title, query, bullets, themes, excerpts } = summary;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        background: CARD_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        padding: 24,
        boxSizing: "border-box",
        color: FG,
        animation: "voice-widget-fade-in 0.28s ease-out both"
      },
      children: [
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 6 }, children: [
          /* @__PURE__ */ jsx("span", { style: { fontSize: 12, color: MUTED, letterSpacing: "1px", textTransform: "uppercase" }, children: "Shopper Insights" }),
          title && /* @__PURE__ */ jsx("h3", { style: { fontSize: 18, fontWeight: 600, margin: 0, color: FG, letterSpacing: "0.08px" }, children: title })
        ] }),
        /* @__PURE__ */ jsx("div", { style: { height: 1, background: BORDER, margin: "20px -24px" } }),
        query && /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              background: SOFT_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 10,
              padding: 16,
              display: "flex",
              alignItems: "center",
              gap: 13,
              marginBottom: 24
            },
            children: [
              /* @__PURE__ */ jsx("span", { style: { flexShrink: 0 }, children: /* @__PURE__ */ jsx(LensIcon, { size: 32 }) }),
              /* @__PURE__ */ jsx("span", { style: { fontSize: 16, fontWeight: 500, color: FG, letterSpacing: "0.08px", lineHeight: "24px" }, children: query })
            ]
          }
        ),
        themes.length > 0 && /* @__PURE__ */ jsxs("div", { style: { marginBottom: 24 }, children: [
          /* @__PURE__ */ jsx("p", { style: { ...SECTION_LABEL, marginBottom: 16 }, children: "Key themes" }),
          /* @__PURE__ */ jsx("div", { style: { display: "flex", flexWrap: "wrap", gap: 8 }, children: themes.map((t, i) => {
            const palette = THEME_PALETTE[t.tone] ?? THEME_PALETTE.positive;
            return /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: "20px",
                  padding: "4px 16px",
                  borderRadius: 100,
                  background: palette.bg,
                  color: palette.fg,
                  whiteSpace: "nowrap"
                },
                children: t.label
              },
              `${i}-${t.label}`
            );
          }) })
        ] }),
        bullets.length > 0 && /* @__PURE__ */ jsxs("div", { style: { marginBottom: 24 }, children: [
          /* @__PURE__ */ jsx("p", { style: { ...SECTION_LABEL, marginBottom: 16 }, children: "What customers are saying" }),
          /* @__PURE__ */ jsx("ul", { style: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 15 }, children: bullets.map((b, i) => /* @__PURE__ */ jsxs(
            "li",
            {
              style: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, color: FG, letterSpacing: "0.08px", lineHeight: "20px" },
              children: [
                /* @__PURE__ */ jsx("span", { style: { flexShrink: 0 }, children: /* @__PURE__ */ jsx(BulletIcon, { tone: b.tone }) }),
                /* @__PURE__ */ jsx("span", { children: b.text })
              ]
            },
            `${i}-${b.text.slice(0, 12)}`
          )) })
        ] }),
        excerpts.length > 0 && /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("p", { style: { ...SECTION_LABEL, marginBottom: 16 }, children: "Highlights" }),
          /* @__PURE__ */ jsx("div", { style: { display: "flex", flexDirection: "column", gap: 14 }, children: excerpts.map((ex, i) => /* @__PURE__ */ jsx(HighlightCard, { excerpt: ex }, `${i}-${(ex.title ?? ex.snippet).slice(0, 12)}`)) })
        ] })
      ]
    }
  );
}
const ReviewInsightsWidget = ({ data }) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        width: "100%",
        maxWidth: 768,
        maxHeight: "100%",
        padding: "16px 12px 8px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflowY: "auto",
        // Inherit the brand-configured body font from the voice overlay root.
        fontFamily: "inherit"
      },
      children: [
        /* @__PURE__ */ jsx("style", { children: WIDGET_KEYFRAMES }),
        data.searches.map((s) => /* @__PURE__ */ jsx(InsightCard, { summary: s }, s.name))
      ]
    }
  );
};
export {
  ReviewInsightsWidget
};
//# sourceMappingURL=ReviewInsightsWidget.js.map
